import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons'
import { faPlus, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC } from 'react'
import { ButtonGroup, Col, Container, Row } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit'
import { useTranslation } from 'react-i18next'
import ProtectedButton from './ProtectedButton'

const DataTable: FC<DataTableProps> = (props) => {
  const { t } = useTranslation()

  const {
    keyField = 'id',
    data,
    columns,
    caption,
    captionButtons = [],
    captionGridSize = 4,
    helpLink,
    noDataIndication = () => <h1 className='text-center text-white'>{t('noDataFound')}</h1>,
    bootstrap4 = true,
    striped = true,
    bordered = false,
    hover = true,
    condensed = false,
    id = Math.floor(Math.random() * 100).toString(),
    tabIndexCell = true,
    classes,
    bodyClasses,
    wrapperClasses = 'table-responsive p-1',
    headerClasses = 'table-dark',
    selectRow,
    expandRow,
    rowStyle,
    rowClasses,
    rowEvents,
    hiddenRows,
    defaultSorted,
    paginate = false,
    searchable = true,
    searchPlaceholder = t('search'),
    showPaginationAllBtn = true,
    sizePerPageList = [
      {
        text: '5',
        value: 5,
      },
      {
        text: '10',
        value: 10,
      },
    ],
  } = props

  const { SearchBar } = Search

  const customTotal = (from: React.ReactNode, to: React.ReactNode, size: React.ReactNode) => (
    <div className='react-bootstrap-table-pagination-total float-md-right mt-1'>
      Showing {from} to {to} of {size} Results
    </div>
  )

  const sizePerPageRenderer = ({ options, currSizePerPage, onSizePerPageChange }: any) => (
    <ButtonGroup className='float-md-left'>
      {options.map((option: any) => {
        const isSelect = currSizePerPage === `${option.page}`
        return (
          <button
            key={option.text}
            type='button'
            onClick={() => onSizePerPageChange(option.page)}
            className={`btn ${isSelect ? 'btn-primary' : 'btn-secondary'}`}>
            {option.text}
          </button>
        )
      })}
    </ButtonGroup>
  )

  const options = {
    alwaysShowAllBtns: true, // Always show next and previous button
    withFirstAndLast: true, // First and Last page button
    hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
    showTotal: true,
    paginationTotalRenderer: customTotal,
    disablePageTitle: true,
    sizePerPageList: sizePerPageList,
    sizePerPageRenderer,
  }

  if (showPaginationAllBtn) {
    options.sizePerPageList.push({
      text: 'All',
      value: data.length,
    })
  }

  const pagination = paginate ? paginationFactory(options) : undefined

  const sortOption = {
    // No need to configure sortCaret per column
    sortCaret: (order: any, column: any) => {
      if (order === 'asc') return <FontAwesomeIcon icon={faSortUp} className='ml-1' />
      if (order === 'desc') return <FontAwesomeIcon icon={faSortDown} className='ml-1' />
      return <FontAwesomeIcon icon={faSort} className='ml-1 text-muted' />
    },
  }

  const captionButtonRender = (
    <Col className='text-right pr-1 ml-nfc-1' lg={captionGridSize}>
      {captionButtons.map((captionButton) => {
        const { onClick, variant = 'primary', text, permissions, icon = faPlus, disabled } = captionButton
        return (
          <ProtectedButton
            variant={variant}
            className='ml-auto my-auto'
            onClick={onClick}
            permissions={permissions}
            disabled={disabled}
            key={Math.floor(Math.random() * 100).toString()}>
            <FontAwesomeIcon icon={icon} /> {text !== undefined && <span className='ml-1 my-auto'>{text}</span>}
          </ProtectedButton>
        )
      })}
    </Col>
  )

  return (
    <ToolkitProvider keyField={keyField} data={data} columns={columns} search>
      {(props: any) => (
        <div className='react-bootstrap-table-search'>
          <Container fluid>
            <Row>
              <Col lg={searchable ? 4 : 12 - captionGridSize}>
                <h3 className='d-inline'>{caption} </h3>
                {helpLink && (
                  <sup>
                    <a target='_blank' href={helpLink} rel='noopener noreferrer'>
                      <FontAwesomeIcon icon={faQuestionCircle} size='lg' />
                    </a>
                  </sup>
                )}
              </Col>
              {searchable && (
                <Col lg={8 - captionGridSize}>
                  <SearchBar {...props.searchProps} placeholder={searchPlaceholder} className='w-100 border-primary' tableId={id} />
                </Col>
              )}
              {captionButtonRender}
            </Row>
          </Container>

          <BootstrapTable
            {...props.baseProps}
            bootstrap4={bootstrap4}
            noDataIndication={noDataIndication}
            striped={striped}
            hover={hover}
            bordered={bordered}
            condensed={condensed}
            id={id}
            tabIndexCell={tabIndexCell}
            classes={classes}
            bodyClasses={bodyClasses}
            wrapperClasses={wrapperClasses}
            headerClasses={headerClasses}
            selectRow={selectRow}
            expandRow={expandRow}
            rowStyle={rowStyle}
            rowClasses={rowClasses}
            rowEvents={rowEvents}
            hiddenRows={hiddenRows}
            defaultSorted={defaultSorted}
            pagination={pagination}
            sort={sortOption}
          />
        </div>
      )}
    </ToolkitProvider>
  )
}

type DataTableProps = {
  keyField?: string
  data: any[]
  columns: any[]
  caption?: String
  captionButtons?: CaptionButton[]
  captionGridSize?: number
  helpLink?: string
  noDataIndication?: Function
  bootstrap4?: boolean
  striped?: boolean
  bordered?: boolean
  hover?: boolean
  condensed?: boolean
  id?: string
  tabIndexCell?: boolean
  classes?: string
  bodyClasses?: string
  wrapperClasses?: string
  headerClasses?: string
  selectRow?: Object
  expandRow?: any
  rowStyle?: Object | Function
  rowClasses?: string | Function
  rowEvents?: Object
  hiddenRows?: any[]
  defaultSorted?: any[]
  paginate?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  showPaginationAllBtn?: boolean
  sizePerPageList?: any[]
}

export type CaptionButton = {
  onClick: () => void
  text?: string
  permissions?: any[]
  icon?: IconDefinition
  disabled?: boolean
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'warning'
    | 'info'
    | 'dark'
    | 'light'
    | 'link'
    | 'outline-primary'
    | 'outline-secondary'
    | 'outline-success'
    | 'outline-danger'
    | 'outline-warning'
    | 'outline-info'
    | 'outline-dark'
    | 'outline-light'
}
export default DataTable
